import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
<LegalPage title="Privacy Policy" lastUpdated="March 2026">
  <Section title="1. Information We Collect">
    <p>
      We collect information you provide directly (such as name, email, profile details, and business information) and information generated through your use of the service (including tasks, documents, logs, and workspace activity).
      We may also collect device and network data (IP address, browser, session data), integration data from connected third-party services, billing and transaction data processed via Stripe, and AI-related data such as prompts, generated content, and associated metadata.
    </p>
  </Section>

  <Section title="2. How We Use Your Information">
    <p>
      We use your information to provide, operate, and improve the service; authenticate users; execute workflows and AI-driven tasks on your behalf; generate content; manage billing and payments; communicate with you; and monitor performance, reliability, and security.
    </p>
  </Section>

  <Section title="3. Data Storage">
    <p>
      Your data is stored on secure cloud infrastructure providers. Sensitive data such as tokens and credentials are encrypted at rest using industry-standard encryption methods. Payment information is processed by Stripe and we do not store full card details.
    </p>
  </Section>

  <Section title="4. Sharing of Information">
    <p>
      We do not sell your personal information. We may share data with trusted service providers who help operate the platform, including infrastructure providers, payment processors, email delivery services, analytics providers, and error monitoring tools. We may also disclose data where required by law or to protect the rights and safety of our users and platform.
    </p>
  </Section>

  <Section title="5. AI & Third-Party Models">
    <p>
      We use third-party AI model providers (such as OpenAI, Anthropic, and Google) to process prompts, generate content, and execute agent workflows. Relevant data, including prompts and context required for task execution, may be shared with these providers.
    </p>
  </Section>

  <Section title="6. Cookies">
    <p>
      We use cookies and similar technologies to maintain sessions, ensure security, support product functionality, and measure marketing performance. You can control cookies through your browser settings.
    </p>
  </Section>

  <Section title="7. Data Retention">
    <p>
      We retain data for as long as necessary to provide the service. If you delete your account, we apply a soft-delete period of up to 30 days before permanent deletion, unless longer retention is required for legal or operational reasons. Certain data, such as logs and AI-generated content, may be retained for a limited period to support functionality, security, and compliance.
    </p>
  </Section>

  <Section title="8. Your Rights">
    <p>
      Depending on your jurisdiction, you may have rights to access, correct, or delete your personal data, and to opt out of certain data uses. You can manage some of these settings within the product or contact us to make a request.
    </p>
  </Section>

  <Section title="9. Security">
    <p>
      We implement industry-standard technical and organizational safeguards, including encryption of sensitive data and secure handling of credentials. While we take reasonable steps to protect your information, no system can guarantee absolute security.
    </p>
  </Section>

  <Section title="10. Contact">
    <p>
      For privacy-related inquiries or requests, please contact us at privacy@raji.ai.
    </p>
  </Section>
</LegalPage>
  );
}

function LegalPage({ title, lastUpdated, children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)', fontFamily: 'var(--sans)' }}>
      <main style={{ maxWidth: '720px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <Link to="/" style={{ fontFamily: 'var(--serif)', fontSize: '18px',
          color: 'var(--ink)', textDecoration: 'none', display: 'inline-block',
          marginBottom: '32px' }}>
          raji<span style={{ color: 'var(--accent)' }}>.</span>ai
        </Link>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: '36px',
          fontWeight: '400', marginBottom: '8px' }}>{title}</h1>
        <p style={{ fontSize: '13px', color: 'var(--ink-mute)',
          marginBottom: '40px' }}>Last updated: {lastUpdated}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {children}
        </div>
        <div style={{ marginTop: '48px', paddingTop: '24px',
          borderTop: '1px solid var(--paper-3)', fontSize: '12px',
          color: 'var(--ink-mute)' }}>
          <Link to="/terms" style={navLink}>Terms</Link>
          {' · '}
          <Link to="/privacy" style={navLink}>Privacy</Link>
          {' · '}
          <Link to="/" style={navLink}>Back to Raji.ai</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 style={{ fontSize: '16px', fontWeight: '600',
        marginBottom: '8px', color: 'var(--ink)' }}>{title}</h2>
      <div style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--ink-soft)' }}>
        {children}
      </div>
    </div>
  );
}

const navLink = {
  fontSize: '12px', color: 'var(--ink-mute)', textDecoration: 'none',
};